import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createCasaMapaAdminAgency } from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale } from "../../../intl";
import "../../../styles/casamapaAgencyCreate.css";

const COPY = "realEstate.casaMapa.admin.agencies.create";

function slugifyPreview(displayName) {
  return String(displayName || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function FormSection({ title, description, children }) {
  return (
    <section className="cm-agency-create__section">
      <div className="cm-agency-create__section-head">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      <div className="cm-agency-create__section-body">{children}</div>
    </section>
  );
}

export default function CasaMapaAdminAgencyCreatePage() {
  const { path, clientViewId } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState({
    displayName: "",
    slug: "",
    legalName: "",
    description: "",
    websiteUrl: "",
    sourceKeys: "",
    publicationStatus: "published",
    partnerStatus: "none",
  });

  const slugPreview = useMemo(() => {
    const explicit = form.slug.trim();
    if (explicit) return explicit;
    return slugifyPreview(form.displayName);
  }, [form.displayName, form.slug]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!clientViewId || submitting) return;

    setSubmitting(true);
    setErrorMessage("");

    try {
      const result = await createCasaMapaAdminAgency(clientViewId, {
        displayName: form.displayName.trim(),
        slug: form.slug.trim() || undefined,
        legalName: form.legalName.trim() || undefined,
        description: form.description.trim() || undefined,
        websiteUrl: form.websiteUrl.trim() || undefined,
        sourceKeys: form.sourceKeys.trim() || undefined,
        publicationStatus: form.publicationStatus,
        partnerStatus: form.partnerStatus,
      });
      const slug = result.agency?.slug || result.registry?.slug;
      if (slug) {
        navigate(path(`agencies/${slug}`));
        return;
      }
      navigate(path("agencies"));
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.submitError`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="cm-agency-create">
      <header className="cm-agency-create__hero partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
        </div>
        <Link className="partner-agency-card__link" to={path("agencies")}>
          {t(`${COPY}.backToList`)}
        </Link>
      </header>

      <form className="cm-agency-create__layout" onSubmit={handleSubmit}>
        <div className="cm-agency-create__main">
          {errorMessage ? (
            <div className="cm-agency-create__error" role="alert">
              <p>{errorMessage}</p>
            </div>
          ) : null}

          <FormSection title={t(`${COPY}.identityTitle`)} description={t(`${COPY}.identityHint`)}>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.displayName`)}</span>
              <input
                type="text"
                value={form.displayName}
                onChange={(event) => update("displayName", event.target.value)}
                required
                minLength={2}
                autoFocus
              />
            </label>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.slug`)}</span>
              <input
                type="text"
                value={form.slug}
                onChange={(event) => update("slug", event.target.value)}
                placeholder={slugPreview || t(`${COPY}.slugPlaceholder`)}
              />
              <small className="cm-agency-create__hint">
                {t(`${COPY}.slugHint`, { slug: slugPreview || "—" })}
              </small>
            </label>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.legalName`)}</span>
              <input
                type="text"
                value={form.legalName}
                onChange={(event) => update("legalName", event.target.value)}
              />
            </label>
          </FormSection>

          <FormSection title={t(`${COPY}.profileTitle`)} description={t(`${COPY}.profileHint`)}>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.description`)}</span>
              <textarea
                rows={4}
                value={form.description}
                onChange={(event) => update("description", event.target.value)}
              />
            </label>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.websiteUrl`)}</span>
              <input
                type="url"
                value={form.websiteUrl}
                onChange={(event) => update("websiteUrl", event.target.value)}
                placeholder="https://"
              />
            </label>
          </FormSection>

          <FormSection title={t(`${COPY}.inventoryTitle`)} description={t(`${COPY}.inventoryHint`)}>
            <label className="cm-agency-create__field">
              <span>{t(`${COPY}.sourceKeys`)}</span>
              <input
                type="text"
                value={form.sourceKeys}
                onChange={(event) => update("sourceKeys", event.target.value)}
                placeholder={t(`${COPY}.sourceKeysPlaceholder`)}
              />
              <small className="cm-agency-create__hint">{t(`${COPY}.sourceKeysHint`)}</small>
            </label>
          </FormSection>

          <FormSection title={t(`${COPY}.statusTitle`)} description={t(`${COPY}.statusHint`)}>
            <div className="cm-agency-create__grid">
              <label className="cm-agency-create__field">
                <span>{t(`${COPY}.publicationStatus`)}</span>
                <select
                  value={form.publicationStatus}
                  onChange={(event) => update("publicationStatus", event.target.value)}
                >
                  <option value="published">{t(`${COPY}.publication.published`)}</option>
                  <option value="draft">{t(`${COPY}.publication.draft`)}</option>
                </select>
              </label>
              <label className="cm-agency-create__field">
                <span>{t(`${COPY}.partnerStatus`)}</span>
                <select
                  value={form.partnerStatus}
                  onChange={(event) => update("partnerStatus", event.target.value)}
                >
                  <option value="none">{t("realEstate.casaMapa.admin.agencies.partnerStatus.none")}</option>
                  <option value="pending">{t("realEstate.casaMapa.admin.agencies.partnerStatus.pending")}</option>
                  <option value="partner">{t("realEstate.casaMapa.admin.agencies.partnerStatus.partner")}</option>
                  <option value="active">{t("realEstate.casaMapa.admin.agencies.partnerStatus.active")}</option>
                </select>
              </label>
            </div>
          </FormSection>
        </div>

        <aside className="cm-agency-create__aside">
          <div className="cm-agency-create__aside-card">
            <h2>{t(`${COPY}.nextStepsTitle`)}</h2>
            <ul>
              <li>{t(`${COPY}.nextSteps.profile`)}</li>
              <li>{t(`${COPY}.nextSteps.crm`)}</li>
              <li>{t(`${COPY}.nextSteps.inventory`)}</li>
            </ul>
            <button
              type="submit"
              className="partner-dashboard-btn partner-dashboard-btn--primary cm-agency-create__submit"
              disabled={submitting || !form.displayName.trim()}
            >
              {submitting ? t(`${COPY}.submitting`) : t(`${COPY}.submit`)}
            </button>
          </div>
        </aside>
      </form>
    </section>
  );
}
